import { Component } from '@theme/component';

const SWIPE_THRESHOLD = 44;

/**
 * "Story deck" section: a 3D deck of vertical video cards synced with chapter copy.
 * The active card plays (muted unless sound is turned on) while the section is in view and
 * advances to the next chapter when its video ends.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} cards
 * @property {HTMLVideoElement[]} [videos]
 * @property {HTMLElement[]} [chapters]
 * @property {HTMLButtonElement[]} [navButtons]
 * @property {HTMLElement[]} [progressBars]
 * @property {HTMLButtonElement} [soundToggle]
 * @property {HTMLElement} [counter]
 * @property {HTMLElement} deck
 *
 * @extends Component<Refs>
 */
class StoryDeck extends Component {
  requiredRefs = ['cards', 'deck'];

  #active = 0;
  #soundOn = false;
  #inView = false;
  /** @type {{ x: number, y: number } | null} */
  #touchStart = null;
  /** @type {IntersectionObserver | null} */
  #viewObserver = null;
  /** @type {IntersectionObserver | null} */
  #preloadObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.cards.forEach((card, index) => {
      const video = this.#video(index);
      if (!video) return;

      // The card shows its poster image until the video is really rendering frames.
      const markPlaying = () => {
        if (index === this.#active && !video.paused && video.currentTime > 0) card.setAttribute('data-playing', '');
      };
      video.addEventListener('playing', markPlaying);
      video.addEventListener('timeupdate', () => {
        markPlaying();
        this.#updateProgress(index);
      });
      video.addEventListener('ended', () => {
        if (index === this.#active) this.next();
      });
    });

    const { deck } = this.refs;
    deck.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    deck.addEventListener('touchend', this.#handleTouchEnd, { passive: true });

    this.#viewObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#inView = entry.isIntersecting;

        if (this.#inView) {
          this.#render();
        } else {
          this.#pauseAll();
          if (this.#soundOn) this.#setSound(false);
        }
      },
      { threshold: 0.35 }
    );
    this.#viewObserver.observe(this);

    // Warm up the first two videos shortly before the section scrolls into view.
    this.#preloadObserver = new IntersectionObserver(
      ([entry], observer) => {
        if (!entry?.isIntersecting) return;
        this.#load(0);
        this.#load(1);
        observer.disconnect();
      },
      { rootMargin: '500px 0px' }
    );
    this.#preloadObserver.observe(this);

    window.addEventListener('pagehide', this.#pauseAll);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#viewObserver?.disconnect();
    this.#preloadObserver?.disconnect();
    this.refs.deck.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.deck.removeEventListener('touchend', this.#handleTouchEnd);
    window.removeEventListener('pagehide', this.#pauseAll);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#pauseAll();
  }

  get #count() {
    return this.refs.cards.length;
  }

  /**
   * Shows the chapter at `index` (wraps around).
   * @param {number | string} index
   */
  go(index) {
    const previous = this.#active;
    const count = this.#count;
    if (count === 0) return;

    this.#active = ((Number(index) % count) + count) % count;
    if (this.#active === previous) return;

    this.#render(previous);
  }

  next() {
    this.go(this.#active + 1);
  }

  previous() {
    this.go(this.#active - 1);
  }

  toggleSound() {
    this.#setSound(!this.#soundOn);

    const video = this.#video(this.#active);
    if (!video) return;

    video.muted = !this.#soundOn;
    video.volume = 1;
    video.play().catch(() => {});
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  }

  /**
   * @param {number} index
   * @returns {HTMLVideoElement | null}
   */
  #video(index) {
    const card = this.refs.cards[index];
    const video = card?.querySelector('video');
    return video instanceof HTMLVideoElement ? video : null;
  }

  /**
   * @param {number} index
   */
  #load(index) {
    const video = this.#video(index);
    if (!video || video.dataset.loaded) return;

    video.dataset.loaded = 'true';
    if (video.dataset.src && !video.getAttribute('src')) video.setAttribute('src', video.dataset.src);
    video.preload = 'auto';
    video.load();
  }

  /**
   * @param {number} index
   */
  #updateProgress(index) {
    const video = this.#video(index);
    const bar = this.refs.progressBars?.[index];
    if (!video || !bar || index !== this.#active || !video.duration) return;

    bar.style.setProperty('--progress', `${(video.currentTime / video.duration) * 100}%`);
  }

  /**
   * @param {boolean} on
   */
  #setSound(on) {
    this.#soundOn = on;
    this.refs.soundToggle?.setAttribute('aria-pressed', String(on));
  }

  #pauseAll = () => {
    this.refs.cards.forEach((_, index) => {
      const video = this.#video(index);
      if (!video) return;
      video.pause();
      video.muted = true;
    });
  };

  /**
   * Moves every card to its new stacking position. Cards jumping more than one position fade out,
   * move without a transition, then fade back in so they never slide across the deck.
   * @param {number} previous
   */
  #applyPositions(previous) {
    const count = this.#count;

    this.refs.cards.forEach((card, index) => {
      const oldPosition = (index - previous + count) % count;
      const newPosition = (index - this.#active + count) % count;

      if (Math.abs(newPosition - oldPosition) > 1) {
        card.setAttribute('data-fading', '');
        window.setTimeout(() => {
          card.setAttribute('data-no-transition', '');
          card.dataset.position = String(newPosition);
          void card.offsetWidth;
          card.removeAttribute('data-no-transition');
          card.removeAttribute('data-fading');
        }, 320);
      } else {
        card.dataset.position = String(newPosition);
      }
    });
  }

  /**
   * @param {number} [previous]
   */
  #render(previous = this.#active) {
    const { chapters = [], navButtons = [], progressBars = [], counter } = this.refs;
    const count = this.#count;

    this.#applyPositions(previous);

    chapters.forEach((chapter, index) => {
      const active = index === this.#active;
      chapter.hidden = !active;
      chapter.toggleAttribute('data-active', active);
    });

    navButtons.forEach((button, index) => {
      button.setAttribute('aria-selected', String(index === this.#active));
    });

    progressBars.forEach((bar, index) => {
      if (index !== this.#active) bar.style.setProperty('--progress', '0%');
    });

    if (counter) counter.textContent = String(this.#active + 1).padStart(2, '0');

    this.refs.cards.forEach((_, index) => {
      const video = this.#video(index);
      if (!video) return;

      if (index === this.#active) {
        this.#load(index);
        video.muted = !this.#soundOn;
        if (this.#inView) video.play().catch(() => {});
      } else {
        this.refs.cards[index]?.removeAttribute('data-playing');
        video.pause();
        video.muted = true;
        if (video.currentTime > 0) {
          try {
            video.currentTime = 0;
          } catch (error) {
            // Ignore: media not seekable yet.
          }
        }
      }
    });

    if (count > 1) this.#load((this.#active + 1) % count);
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touch = event.touches[0];
    this.#touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!this.#touchStart || !touch) return;

    const dx = touch.clientX - this.#touchStart.x;
    const dy = touch.clientY - this.#touchStart.y;
    this.#touchStart = null;

    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      dx < 0 ? this.next() : this.previous();
    }
  };

  /**
   * Theme editor: selecting a chapter block shows that chapter.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const chapters = this.refs.chapters ?? [];
    const index = chapters.findIndex((chapter) => chapter === event.target);
    if (index !== -1) this.go(index);
  };
}

if (!customElements.get('story-deck')) {
  customElements.define('story-deck', StoryDeck);
}
